import { useState } from "react";
import type { DomainId } from "../content/domains";
import type { RoleId } from "../content/roles";
import { useWaitlist } from "../hooks/useWaitlistForm";
import { trackEvent } from "../lib/analytics";
import { Button } from "./Button";
import { DomainSelector } from "./DomainSelector";
import { PostSubmitPanel } from "./PostSubmitPanel";
import { RoleSelector } from "./RoleSelector";

interface WaitlistFormProps {
  location: "hero" | "footer" | "portal";
  ctaLabel?: string;
}

export function WaitlistForm({ location, ctaLabel = "Join the waitlist" }: WaitlistFormProps) {
  const { status, error, submit } = useWaitlist();

  const [email, setEmail] = useState("");
  const [domains, setDomains] = useState<DomainId[]>([]);
  const [domainOther, setDomainOther] = useState("");
  const [role, setRole] = useState<RoleId>("");
  const [honeypot, setHoneypot] = useState("");
  // Hero stays compact-on-load to keep the eyebrow/headline weight balanced;
  // every other surface (footer-cta, portal-section CTA, modal) is a focused
  // sign-up area where the full form should be visible immediately.
  const [expanded, setExpanded] = useState(location !== "hero");

  if (status === "submitted") {
    return <PostSubmitPanel />;
  }

  const submitting = status === "submitting";

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || submitting) return;
    trackEvent("cta_click", { location: `${location}_submit` });
    void submit({
      email,
      domains,
      domainOther: domainOther.trim() || undefined,
      role,
      source: location,
      honeypot,
    });
  };

  return (
    <form className={`waitlist-form waitlist-form--${location}`} onSubmit={onSubmit} noValidate>
      {/* Honeypot — hidden from real users, bots fill every field */}
      <div className="waitlist-form__honeypot" aria-hidden="true">
        <label>
          Company website
          <input
            type="text"
            name="company_website"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </label>
      </div>

      <div className="waitlist-form__row">
        <input
          type="email"
          className="input"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (!expanded) setExpanded(true);
          }}
          onFocus={() => setExpanded(true)}
          required
          aria-label="Email"
        />
        <Button type="submit" variant="orange" size="lg" disabled={submitting || !email}>
          {submitting ? "Submitting…" : ctaLabel}
        </Button>
      </div>

      {expanded ? (
        <div className="waitlist-form__details">
          <DomainSelector
            selected={domains}
            onChange={setDomains}
            otherText={domainOther}
            onOtherTextChange={setDomainOther}
          />
          <RoleSelector value={role} onChange={setRole} />
        </div>
      ) : null}

      {status === "error" && error ? (
        <div className="waitlist-form__error" role="alert">
          {error}
        </div>
      ) : null}
    </form>
  );
}
